import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import { PageSizes } from "../../../common/constants";
import { ExportFileType } from "../../../common/enums";
import ContentWrapper from "../../../common/modules/wrappers/ContentWrapper";
import { appendSearchParamsToURL, numberOrZero } from "../../../common/utils/utils";
import type { UUID } from "../../../typings/global";
import { AgentListFilterView } from "../components/views/list/AgentListFilterView";
import { AgentListTableView } from "../components/views/list/AgentListTableView";
import {
  deleteStateAgentsPageAction,
  downloadAgentsExportActions,
  filterAgentsActions,
  selectAgentsCurrentPage
} from "../ducks";
import { AgentSecondaryStatus, AgentStatus } from "../enums";
import { AgentFilterPageRequest } from "../types";

export const AgentListContainer = () => {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();

  const agentsCurrentPage = useSelector(selectAgentsCurrentPage);

  const dispatch = useDispatch();
  const actions = useMemo(
    () =>
      bindActionCreators(
        {
          filterAgents: filterAgentsActions.request,
          downloadAgentsExport: downloadAgentsExportActions.request,
          deleteStateAgentsPage: deleteStateAgentsPageAction
        },
        dispatch
      ),
    [dispatch]
  );

  useEffect(() => {
    actions.filterAgents({
      pageIndex: numberOrZero(urlParams.get("pageIndex")),
      pageSize: PageSizes.HUGE,
      keyword: urlParams.get("keyword") ?? undefined,
      commissionsSettingsLevelIds: urlParams.getAll("commissionsSettingsLevelIds") as UUID[],
      commissionsSettingsRankIds: urlParams.getAll("commissionsSettingsRankIds") as UUID[],
      statuses: urlParams.getAll("statuses") as AgentStatus[],
      secondaryStatuses: urlParams.getAll("secondaryStatuses") as AgentSecondaryStatus[],
      agentCreatedAtDateMin: urlParams.get("agentCreatedAtDateMin") ?? undefined,
      rootAgentId: urlParams.get("rootAgentId") as UUID,
      onlyDirectSubordinates: urlParams.get("onlyDirectSubordinates") === "true" ? true : undefined,
      includeDeactivated: urlParams.get("includeDeactivated") === "false" ? false : undefined,
      includeRepresentatives: urlParams.get("includeRepresentatives") === "false" ? false : undefined,
      includeNonGainers: urlParams.get("includeNonGainers") === "false" ? false : undefined
    });
    return () => {
      actions.deleteStateAgentsPage();
    };
  }, []);

  const handleFilterSubmit = (filter: AgentFilterPageRequest): void => {
    const urlParams = {
      ...filter,
      keyword: filter.keyword !== "" ? filter.keyword : undefined,
      onlyDirectSubordinates: filter.onlyDirectSubordinates || undefined,
      includeDeactivated: filter.includeDeactivated ? undefined : false,
      includeRepresentatives: filter.includeRepresentatives ? undefined : false,
      includeNonGainers: filter.includeNonGainers ? undefined : false
    } as AgentFilterPageRequest;

    navigate(appendSearchParamsToURL({ ...urlParams, pageIndex: undefined }), { replace: true });
    actions.filterAgents({
      ...urlParams,
      pageIndex: 0,
      pageSize: agentsCurrentPage.pageSize
    });
  };

  const handleTablePageChange = (pageNumber: number): void => {
    const {
      pageSize,
      keyword,
      commissionsSettingsLevelIds,
      commissionsSettingsRankIds,
      statuses,
      secondaryStatuses,
      agentCreatedAtDateMin,
      rootAgentId,
      onlyDirectSubordinates,
      includeDeactivated,
      includeRepresentatives,
      includeNonGainers
    } = agentsCurrentPage;

    navigate(appendSearchParamsToURL({ pageIndex: pageNumber - 1 }), { replace: true });
    actions.filterAgents({
      pageIndex: pageNumber - 1,
      pageSize,
      keyword,
      commissionsSettingsLevelIds,
      commissionsSettingsRankIds,
      statuses,
      secondaryStatuses,
      agentCreatedAtDateMin,
      rootAgentId,
      onlyDirectSubordinates,
      includeDeactivated,
      includeRepresentatives,
      includeNonGainers
    });
  };

  const handleExportClick = (exportFileType: ExportFileType): void => {
    const {
      keyword,
      commissionsSettingsLevelIds,
      commissionsSettingsRankIds,
      statuses,
      secondaryStatuses,
      agentCreatedAtDateMin,
      rootAgentId,
      onlyDirectSubordinates,
      includeDeactivated,
      includeRepresentatives,
      includeNonGainers
    } = agentsCurrentPage;

    actions.downloadAgentsExport({
      keyword,
      commissionsSettingsLevelIds,
      commissionsSettingsRankIds,
      statuses,
      secondaryStatuses,
      agentCreatedAtDateMin,
      rootAgentId,
      onlyDirectSubordinates,
      includeDeactivated,
      includeRepresentatives,
      includeNonGainers,
      exportFileType
    });
  };

  return (
    <ContentWrapper>
      <AgentListFilterView filter={agentsCurrentPage} onFilterSubmit={handleFilterSubmit} />

      <AgentListTableView
        agentsPage={agentsCurrentPage}
        onPageChange={handleTablePageChange}
        onExportClick={handleExportClick}
      />
    </ContentWrapper>
  );
};
